import {SlctFormat, SlctInternalProps, SlctMultiProps, SlctOption} from 'module/Slct/type/SlctType'
import React, {DragEvent, DragEventHandler, FocusEventHandler, KeyboardEvent, MouseEvent, useCallback, useMemo} from 'react'
import {CircularProgress} from '@mui/material'
import {log} from 'common/log'
import {getOptionLabel} from 'module/Slct/util/getOptionLabel'
import {Key} from 'util/Key'
import {Clear} from '@mui/icons-material'
import {memoAddAction} from 'module/Memo/action/MemoAction'
import {useDispatch, useSelector} from 'react-redux'
import {selMemoMap} from 'module/Memo/selector/MemoSelector'
import {cssCFInputBorder, cssCFInputBorderFocussed} from 'module/CustomField/style/cssCFInput'
import {arrayMoveImmutable} from 'array-move'
import {
	cssSlctChildBase,
	cssSlctClearButton,
	cssSlctEllipsis,
	cssSlctInput,
	cssSlctInputLoadingWrapper,
	cssSlctMultiButtonRegion,
	cssSlctMultiChild1,
	cssSlctMultiChild2,
	cssSlctMultiDisabled,
	cssSlctMultiEnabled,
	cssSlctMultiWrapper,
	cssSlctPlaceholderMulti,
	cssSlctPlaceholderText,
	cssSlctSingleChild,
	cssSlctWrapper,
} from 'module/Slct/style/SlctStyle'
import {getNextCursor} from 'module/Slct/util/getNextCursor'
import {isMemorizable} from 'module/Memo/type/Memorizable'
import {SlctMultiDisplayValueComponent} from 'module/Slct/component/SlctMultiDisplayValueComponent'
import {useSlctProps} from 'module/Slct/hook/useSlctProps'
import {useSlctChangeState} from 'module/Slct/hook/useSlctChangeState'
import {useSlctSharedCallbacks} from 'module/Slct/hook/useSlctSharedCallbacks'
import {SlctMenuComponent} from 'module/Slct/component/SlctMenuComponent'
import {getTextAlignCSS} from 'module/CustomField/style/CFAlignmentStyle'

/* use memo functions to pass props to this component */
export const SlctMulti = React.memo(function SlctMulti<ModelType extends object | void, ValueType extends string | number>(props: SlctMultiProps<ModelType, ValueType>) {
	const dispatch = useDispatch()
	let {
		values, onChange: _onChange,
		options: optionsUnfiltered,
		filterOptions,
		onChangeState,
		displayFormat = SlctFormat.ValueLabel, disableManualSort,
		preventCloseMenuOnSelect, menuWidth, menuHeight,
		disabled, loading,
		placeholderHTML = '', autoFocus, tabIndex = 0, align,
		menuPortal,
	} = props

	if (preventCloseMenuOnSelect === undefined) {
		preventCloseMenuOnSelect = true
		props = {...props, preventCloseMenuOnSelect}
	}

	const {
		byValue,
		inputValue, setInputValue,
		cursor, setCursor,
		focus, setFocus,
		menuOpen, setMenuOpen,
		memoKey, memoSize, memoAutoAdd,
		hasMenu,
		ref,
		refInput, setRefInput,
		refMenu,
		refList,
	} = useSlctProps(props)

	const memoOpts = (useSelector(selMemoMap)?.[memoKey || ''] || []) as SlctOption<ModelType, ValueType>[]

	const onChange = useCallback((o: SlctOption<ModelType, ValueType>[]) => {
		log.trace('Slct _onChange', o)
		if (disabled) return;
		o = o.filter(x => !x.disabled)
		_onChange(o)
	}, [_onChange, disabled])

	const addValue = useCallback((o: SlctOption<ModelType, ValueType>) => {
		log.trace('Slct addValue', o)
		if (!o || !o.value && o.value !== 0) return;
		if (!preventCloseMenuOnSelect) {
			setMenuOpen(false)
			setCursor(null)
		}
		onChange([o, ...values.flatMap(v => v == o.value ? [] : [byValue[v] || {value: v}]) as SlctOption<ModelType, ValueType>[]])
		if (memoAutoAdd && memoKey && memoSize && isMemorizable(o)) {
			dispatch(memoAddAction({memoKey, memoSize, element: o}))
		}
	}, [onChange, values, byValue, memoAutoAdd, memoKey, memoSize, preventCloseMenuOnSelect, setMenuOpen, setCursor])

	const removeValue = useCallback((o: SlctOption<ModelType, ValueType>) => {
		log.trace('Slct removeValue', o)
		if (!o || !o.value && o.value !== 0) return;
		onChange(values.flatMap(v => v == o.value ? [] : [byValue[v] || {value: v}]) as SlctOption<ModelType, ValueType>[])
	}, [onChange, values, byValue])

	const options = useMemo(() => {
		let opts = [
			...values.map(value => ({
				...byValue[value],
				value,
				memo: !!memoOpts.find(m => m.value === value),
				active: true,
			}) as SlctOption<ModelType, ValueType>),
			// memo options without active values
			...memoOpts.flatMap(x => values.includes(x.value as ValueType) ? [] : [({...x, memo: true}) as SlctOption<ModelType, ValueType>]),
			// other values without memo and active
			...optionsUnfiltered.filter(o => !memoOpts.some(x => x.value === o.value) && !values.includes(o.value as ValueType)),
		]
		return !filterOptions || !inputValue.trim() ? opts : filterOptions(opts, inputValue)
	}, [optionsUnfiltered, filterOptions, inputValue, memoOpts, values, byValue])
	const maxIndex = options.length - 1

	useSlctChangeState(onChangeState, inputValue, focus)

	const {
		onClickInput,
		onChangeInput,
		onKeyUp,
	} = useSlctSharedCallbacks(setMenuOpen, setInputValue, refInput)

	const onFocus: FocusEventHandler<HTMLDivElement> = useCallback((evt) => {
		log.trace('Slct onInputFocus', evt)
		setFocus(true)
		setMenuOpen(true)
	}, [setFocus, setMenuOpen])

	const onBlur: FocusEventHandler<HTMLElement> = useCallback((evt) => {
		const focusedMenu = refMenu.current?.contains(evt.relatedTarget)
		const focusedInput = refInput.current?.contains(evt.relatedTarget)
		if (focusedMenu || focusedInput) {
			log.trace('Slct onBlur cancel', evt)
			evt.preventDefault()
			evt.stopPropagation()
			refInput.current?.focus()
			return false
		}
		log.trace('Slct onBlur blur', evt)
		// clicked / tabbed outside
		setMenuOpen(false)
		setCursor(null)
		setFocus(false)
		setInputValue('')
	}, [refInput.current, refMenu.current, setFocus, setMenuOpen, setCursor])

	const onClear = useCallback((event: MouseEvent<HTMLDivElement>) => {
		if (event.button !== 0) return;
		log.trace('Slct onClear')
		onChange([])
	}, [onChange])

	const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
		log.trace('Slct onKeyDown', event)
		if (event.key === Key.Tab) {
			// tab does not select in multi select!
			return;
		} else if (event.key === Key.Enter && cursor !== null) {
			const o = options[cursor]
			if (o) {
				if (!o.active) {
					addValue(o)
				} else {
					removeValue(o)
				}
			}
			return;
		} else if (event.key === Key.Escape) {
			setInputValue('')
			setFocus(false)
			setMenuOpen(false)
			setCursor(null)
			refInput.current?.blur()
			return;
		} else if (event.target === refMenu.current && event.code.match(/^(Numpad|Digit|Key).*$/)) {
			// setInputValue(v => v + evt.key)
			refInput.current?.focus()
			return;
		}
		if (event.ctrlKey || event.altKey || event.shiftKey) return;
		const nextCursor = getNextCursor(event, cursor, maxIndex)
		if (nextCursor !== null) refList.current?.scrollToItem(nextCursor, "smart")
		setCursor(nextCursor)
		log.debug('Slct set cursor / scroll to item', menuOpen, inputValue)
	}, [refInput.current, refMenu.current, refList.current, addValue, removeValue, maxIndex, cursor, options, setFocus, setMenuOpen, setCursor])

	const onClickOption = useCallback((o: SlctOption<ModelType, ValueType> | null) => {
		if (!o || o.disabled) return;
		log.trace('Slct onChange option click', o)
		if (o.active) removeValue(o)
		else addValue(o)
	}, [addValue, removeValue])

	const onMoveOption = useCallback((currentIndex: number, nextIndex: number, event: DragEvent<HTMLDivElement>) => {
		const len = values.length
		if (disableManualSort || currentIndex < 0 || currentIndex >= len || nextIndex < 0 || nextIndex >= len) return;
		onChange(arrayMoveImmutable(values, currentIndex, nextIndex).map(value => byValue[value] || {value}) as SlctOption<ModelType, ValueType>[])
	}, [onChange, values, byValue])

	const itemData: SlctInternalProps<ModelType, ValueType> = {
		...props,
		// new, internal props
		inputValue,
		focus,
		cursor, setCursor,
		//overridden before
		displayFormat,
		options,
		onClickOption,
		refInput,
	}

	const onClickWrapper = useCallback(() => refInput.current?.focus(), [refInput.current])

	const onDragEnd: DragEventHandler<HTMLDivElement> | undefined = useCallback(() => {
		refInput.current?.focus()
	}, [refInput.current])

	const displayValueMappedList = useMemo(() => values.map(value => {
		const o = byValue[value] || {value} as SlctOption<ModelType, ValueType>
		return {
			option: {...o, active: true},
			displayValue: getOptionLabel(displayFormat, o),
		}
	}), [values, byValue])

	const isEmpty = !values.length

	return <>
		<div
			className={'SlctWrapper'}
			data-cursor-key-needs-modifier={hasMenu}
			css={[
				cssSlctWrapper,
				cssSlctMultiWrapper,
				!disabled ? cssSlctMultiEnabled : cssSlctMultiDisabled,
				focus ? cssCFInputBorderFocussed : cssCFInputBorder,
			]}
			onFocus={onFocus}
			onDragEnd={onDragEnd}
			onClick={onClickWrapper}
			ref={ref}
		>
			<input
				className={'SlctInput'}
				css={[
					cssSlctMultiChild2,
					cssSlctInput,
					getTextAlignCSS(align),
				]}
				ref={setRefInput}
				autoFocus={autoFocus}
				tabIndex={tabIndex}
				value={focus ? inputValue : ''}
				onChange={onChangeInput}
				onClick={onClickInput}
				onBlur={onBlur}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				disabled={disabled}
			/>
			<div
				className={'SlctPlaceholder'}
				tabIndex={-1}
				css={[
					cssSlctChildBase,
					cssSlctPlaceholderMulti,
					!disabled ? cssSlctMultiChild1 : cssSlctSingleChild,
					getTextAlignCSS(align),
				]}
			>
				{isEmpty ?
					<div
						css={[cssSlctPlaceholderText, cssSlctEllipsis]}
						dangerouslySetInnerHTML={{__html: placeholderHTML || ''}}
					/>
					:
					displayValueMappedList.map((dv, index) => <SlctMultiDisplayValueComponent
						key={index}
						displayValue={dv.displayValue}
						option={dv.option}
						index={index}
						removeValue={removeValue}
						onMoveOption={onMoveOption}
						disabled={disabled}
						align={align}
					/>)
				}
			</div>
			<div css={cssSlctMultiButtonRegion}>
				{!isEmpty && !disabled &&
					<div
						css={cssSlctClearButton}
						onClick={onClear}
					>
						<Clear fontSize={'inherit'}/>
					</div>
				}
				{loading &&
					<div className={'SlctLoading'} css={cssSlctInputLoadingWrapper}>
						<CircularProgress size={'inherit'} color={'inherit'}/>
					</div>
				}
			</div>
		</div>
		<SlctMenuComponent
			menuOpen={menuOpen}
			loading={loading}
			tabIndex={tabIndex}
			itemData={itemData}
			menuWidth={menuWidth}
			menuHeight={menuHeight}
			refWrapper={ref}
			refInput={refInput}
			refMenu={refMenu}
			refList={refList}
			menuPortal={menuPortal}
		/>
	</>
})